"use client";

import { useEffect, useRef } from "react";
import {
  useNamespacedTranslations,
  useRegisterFilter,
  useSingleValueParameter,
  type FilterControllerProps,
  type FilterMenuItem,
  type ActiveChip,
  SingleSelectFilterMenu,
} from "@zapier/shared";

type LastRunPreset =
  | "within_1"
  | "within_7"
  | "within_30"
  | "within_60"
  | "over_60";

const VALID_LAST_RUN = new Set<LastRunPreset>([
  "within_1",
  "within_7",
  "within_30",
  "within_60",
  "over_60",
]);

const ICON = "miscClock";

const PRESETS: LastRunPreset[] = [
  "within_1",
  "within_7",
  "within_30",
  "within_60",
  "over_60",
];

export function LastRunFilterController({
  isOpen,
  namespace,
  onClose,
  onInteraction,
}: FilterControllerProps): JSX.Element | null {
  const t = useNamespacedTranslations(namespace);
  const lastRun = useSingleValueParameter<LastRunPreset>("lastRun", {
    validValues: VALID_LAST_RUN,
  });

  // Deferred close: keep the menu open until the URL value reflects the pick,
  // then close — mirrors old ZapFilterBar closeOnNextValueReference (lines 234-246).
  const closeOnNextValue = useRef(false);
  useEffect(() => {
    if (closeOnNextValue.current && lastRun.value) {
      closeOnNextValue.current = false;
      onClose();
    }
  }, [lastRun.value, onClose]);

  // Chip labels ported from useFilterChipLabels getLastRunChipLabel / getLastRunChipAriaLabel.
  const label = lastRun.value ? t(`lastRun.preset.${lastRun.value}`) : "";
  const ariaLabel = lastRun.value
    ? t(`lastRun.chipAriaLabel.${lastRun.value}`)
    : "";

  const hasValue = !!lastRun.value;
  let chip: ActiveChip | undefined;
  let signature = "lastRun:none";
  if (hasValue) {
    chip = {
      key: "lastRun",
      iconName: ICON,
      title: t("lastRun.label"),
      label,
      ariaLabel,
    };
    signature = `lastRun:${lastRun.value}`;
  } else if (isOpen) {
    chip = {
      key: "lastRun",
      iconName: ICON,
      title: t("lastRun.label"),
      ariaLabel: t("lastRun.label"),
      isDraft: true,
    };
    signature = "lastRun:draft";
  }

  const items: FilterMenuItem[] = PRESETS.map((preset) => ({
    id: preset,
    name: t(`lastRun.preset.${preset}`),
  }));

  useRegisterFilter({
    type: "lastRun",
    hasValue,
    signature,
    chip,
    renderMenu: () => (
      <SingleSelectFilterMenu
        label={t("lastRun.label")}
        items={items}
        selectedId={lastRun.value}
        onSelect={(id) => {
          if (!VALID_LAST_RUN.has(id as LastRunPreset)) {
            onClose();
            return;
          }
          // setParams no-ops when the URL is unchanged, so deferred close never
          // runs — close immediately when the already-active option is reselected.
          if (id === lastRun.value) {
            onClose();
            return;
          }
          closeOnNextValue.current = true;
          lastRun.set(id as LastRunPreset);
          onInteraction?.();
        }}
      />
    ),
    // React Aria submenu (Filters-button path): RA closes the tree on select.
    menuOptions: {
      label: t("lastRun.label"),
      items,
      selectedId: lastRun.value,
      onSelect: (id) => {
        if (VALID_LAST_RUN.has(id as LastRunPreset) && id !== lastRun.value) {
          lastRun.set(id as LastRunPreset);
          onInteraction?.();
        }
      },
    },
  });
  // eslint-disable-next-line unicorn/no-null -- headless controller renders nothing
  return null;
}
